import type { APMAsset, ICDFView } from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type { Filters, Sort } from '@cognite/fdm-client';
import type {
  Asset,
  AssetAggregatedProperty,
  AssetFilterProps,
  CogniteClient,
  DirectRelationReference,
  ViewReference,
} from '@cognite/sdk';

import { getCdmViewReferencesFromModelVersion } from '../../providers/fdm-services/cdm-view-reference-utils';
import { baseIdmAssetViewReference } from '../../providers/fdm-services/consts';
import type { AssetPropertiesCardFieldConfig } from '../app-config/cdm-types';

import type {
  HierarchyQueryOptions,
  IAssetService,
} from './asset-service-interface';
import type { AssetListSortFilters } from './types';
import { convertAPMAssetToAsset } from './utils/convertAPMAssetToAsset';
import { mapInstanceAggregateItemsToParentChildCounts } from './utils/mapInstanceAggregateItemsToParentChildCounts';

export const LIST_ASSETS_WITH_SORT_FILTERS_AND_AGGREGATED_PROPERTIES_LIMIT = 1000;

export type APMClientDependencies = {
  assets: Pick<ICDFView<APMAsset>, 'list'> & {
    viewReference: Pick<ViewReference, 'version'>;
  };
};

export type CogniteClientDependencies = Pick<
  CogniteClient,
  'post' | 'getBaseUrl' | 'project'
> & {
  instances: Pick<
    CogniteClient['instances'],
    'search' | 'aggregate' | 'retrieve'
  >;
  views: Pick<CogniteClient['views'], 'retrieve'>;
  assets: Pick<CogniteClient['assets'], 'list' | 'retrieve' | 'search'>;
};

export class CdmAssetService extends FDMClient implements IAssetService {
  client: CogniteClientDependencies;
  apmClient: APMClientDependencies;
  instanceSpace: string;
  readonly rootLocation?: DirectRelationReference;
  readonly propertiesCardViewReference: ViewReference;
  readonly propertiesCardConfig?: Record<
    string,
    AssetPropertiesCardFieldConfig
  >;
  readonly hierarchyViewReference: ViewReference = {
    ...baseIdmAssetViewReference,
    type: 'view',
  };

  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies,
    instanceSpace: string,
    propertiesCardViewReference?: ViewReference,
    rootLocation?: DirectRelationReference,
    propertiesCardConfig?: Record<string, AssetPropertiesCardFieldConfig>
  ) {
    super(client);
    this.client = client;
    this.apmClient = apmClient;
    this.instanceSpace = instanceSpace;
    this.rootLocation = rootLocation;
    this.propertiesCardViewReference =
      propertiesCardViewReference ??
      getCdmViewReferencesFromModelVersion().infieldAsset;
    this.propertiesCardConfig = propertiesCardConfig;
  }

  /**
   * Fetches APM assets by externalId across all spaces
   * @param assetExternalIds - Array of asset external IDs to fetch
   * @returns Array of APM assets matching the provided external IDs
   */
  async getAPMAssetByExternalIds(
    assetExternalIds: string[],
    _akerbpCustomFilter?: boolean
  ): Promise<APMAsset[]> {
    if (assetExternalIds.length === 0) return [];
    const uniqueAssetExternalIds = [...new Set(assetExternalIds)];
    const { items: assets } = await this.apmClient.assets.list({
      filters: {
        in: {
          property: 'externalId',
          in: uniqueAssetExternalIds,
        },
      },
      spaces: [], // Query across all spaces
      // The API defaults to 100 results, which silently drops assets. 1000
      // matches the pageSize checklist items are fetched with, so a checklist
      // can never reference more assets than this returns. No pagination here.
      pageSize: 1000,
    });
    return assets;
  }

  // Legacy methods maintained for backward compatibility - these were used in IDM projects and wrapping APM calls is easier than updating all components
  async getAssetByExternalId(
    assetExternalId: string,
    _akerbpCustomFilter?: boolean
  ): Promise<Asset> {
    const [assetData] = await this.getAPMAssetByExternalIds([assetExternalId]);
    if (!assetData) {
      throw new Error(`Asset with external ID '${assetExternalId}' not found`);
    }
    return convertAPMAssetToAsset(assetData);
  }

  // Legacy methods maintained for backward compatibility - these were used in IDM projects and wrapping APM calls is easier than updating all components
  async getAssetByExternalIds(assetExternalIds: string[]): Promise<Asset[]> {
    const assets = await this.getAPMAssetByExternalIds(assetExternalIds);
    return assets.map(convertAPMAssetToAsset);
  }

  async getAssetById(_assetId: number): Promise<Asset> {
    throw new Error('Not implemented');
  }

  async getAssets(
    _filter?: AssetFilterProps,
    _akerbpCustomFilter?: boolean
  ): Promise<Asset[]> {
    throw new Error('Not implemented');
  }

  async getAssetByName(
    _name: string,
    _cdfClassicFilters: AssetFilterProps | undefined
  ): Promise<Asset> {
    throw new Error('Not implemented');
  }

  /**
   * TODO(INFIELD2-2548): Replace this method's filter with new upcoming solution (isRoot property), since this filter is not performant for large numbers of assets (e.g. > 100 000).
   * @returns Root assets across all spaces
   */
  async getRootAPMAssets(): Promise<APMAsset[]> {
    const rootAssets = await this.apmClient.assets.list({
      filters: {
        nested: {
          scope: 'parent',
          filters: {
            isNull: {
              property: 'externalId',
              isNull: true,
            },
          },
        },
      },
      spaces: [], // Query across all spaces
    });
    return rootAssets.items;
  }

  async getRootAssets(): Promise<Asset[]> {
    return this.getRootAPMAssets().then((assets) =>
      assets.map(convertAPMAssetToAsset)
    );
  }

  async getAPMAsset(
    filters?: Filters,
    sort?: Sort,
    pageSize = 1000
  ): Promise<APMAsset | undefined> {
    const {
      items: [asset],
    } = await this.apmClient.assets.list({ filters, sort, pageSize });
    return asset;
  }

  async listAPMAssets(
    filters?: Filters,
    sort?: Sort,
    pageSize = 1000,
    spaces?: string[]
  ): Promise<APMAsset[]> {
    const { items: assets } = await this.apmClient.assets.list({
      filters,
      sort,
      pageSize,
      spaces,
    });
    return assets;
  }

  async searchAssets(
    _query?: string,
    _limit?: number,
    _filter?: AssetFilterProps,
    _akerbpCustomFilter?: boolean
  ): Promise<Asset[]> {
    throw new Error('Not implemented');
  }

  async getAPMAssetByName(name: string, filter?: Filters): Promise<APMAsset> {
    const filters: Filters[] = [];

    if (filter) {
      filters.push({ ...filter });
    }
    const nameFilter = {
      equals: {
        property: 'title',
        eq: name,
      },
    };
    filters.push(nameFilter);

    const {
      items: [asset],
    } = await this.apmClient.assets.list({
      filters: {
        and: [...filters],
      },
    });

    return asset;
  }

  /**
   * Retrieves a list of assets based on a filter, with aggregated properties to build asset hierarchy.
   *
   * @param filter - The filter to apply to the asset list.
   * @param aggregatedProperties - A list of aggregated properties to include in the response.
   * @param limit - The maximum number of assets to retrieve.
   * @returns A promise that resolves to an array of Assets.
   */
  async getAssetsWithAggregatedProps(
    _filter: AssetFilterProps,
    _aggregatedProperties: AssetAggregatedProperty[],
    _akerbpCustomFilter?: boolean,
    _limit?: number
  ): Promise<Asset[]> {
    throw new Error('Not implemented');
  }

  /**
   * Retrieves assets by their InternalIDs or ExternalIDs, with support for aggregated properties.
   *
   * @param assetIds - An array of asset's InternalIDs or ExternalIDs to retrieve.
   * @param aggregatedProperties - A list of aggregated properties to include in the response.
   * @returns A promise that resolves to an array of Assets.
   */
  async getAssetsByInternalExternalIDsWithAggregatedProps(
    _assetIds: number[] | string[],
    _aggregatedProperties: AssetAggregatedProperty[]
  ): Promise<Asset[]> {
    throw new Error('Not implemented');
  }

  /**
   * Lists assets with support for advanced filtering and aggregated properties.
   * Existing Search Assets doesn't support aggregated properties. So we are leveraging the List Assets endpoint with advanced filtering to achieve this.
   * In Javascript SDK, the type for Advanced Filter is not available, so we are hard coding it here.
   *
   * @param filters - A filter to apply to the list.
   * @param customSortFilters - An array of custom sort filters to apply to the results. Results are sorted by '_score_' by default, with custom sort filters appended.
   * @param aggregatedProperties - A list of aggregated properties to include in the response. Defaults to 'childCount' and 'path'.
   * @param limit - The maximum number of results to retrieve. Defaults to 500.
   * @returns A promise that resolves to an array of matching assets sorted by the sort filters, or an empty array if no assets are found.
   */
  async listAssetsWithSortFiltersAndAggregatedProperties(
    _filters: AssetFilterProps,
    _customSortFilters: AssetListSortFilters[] = [],
    _aggregatedProperties: AssetAggregatedProperty[],
    _limit?: number
  ): Promise<Asset[]> {
    throw new Error('Not implemented');
  }

  private hierarchyViewProperty(name: string, view?: ViewReference): string[] {
    const viewReference = view ?? this.hierarchyViewReference;
    return [
      viewReference.space,
      `${viewReference.externalId}/${viewReference.version}`,
      name,
    ];
  }

  async getRootInstances(options: HierarchyQueryOptions = {}) {
    // When a root location is configured, mirror classic assetSubtreeIds behaviour:
    // scope the top of the hierarchy to the direct children of the configured root
    // location asset, instead of every parent-less node in the instance space. This
    // prevents assets from other locations leaking into the tree.
    if (this.rootLocation) {
      return this.getInstancesByIds([this.rootLocation], {
        viewReference: options?.viewReference,
      });
    }

    const { limit = 1000, viewReference } = options;
    const view = viewReference ?? this.hierarchyViewReference;
    const { items } = await this.client.instances.search({
      view,
      instanceType: 'node',
      filter: {
        and: [
          {
            not: {
              exists: { property: this.hierarchyViewProperty('parent', view) },
            },
          },
          {
            equals: {
              property: ['node', 'space'],
              value: this.instanceSpace,
            },
          },
        ],
      },
      limit,
      operator: 'AND',
    });

    return items;
  }

  async getChildInstances(
    parent: DirectRelationReference,
    options: HierarchyQueryOptions = {}
  ) {
    const { limit = 1000, viewReference } = options;
    const view = viewReference ?? this.hierarchyViewReference;
    const { items } = await this.client.instances.search({
      view,
      instanceType: 'node',
      filter: {
        and: [
          {
            equals: {
              property: this.hierarchyViewProperty('parent', view),
              value: parent,
            },
          },
          {
            equals: {
              property: ['node', 'space'],
              value: this.instanceSpace,
            },
          },
        ],
      },
      limit,
      operator: 'AND',
    });
    return items;
  }

  async getChildrenCounts(
    parents: DirectRelationReference[],
    options: HierarchyQueryOptions = {}
  ) {
    if (parents.length === 0) return {};
    const { limit = 1000, viewReference } = options;
    const view = viewReference ?? this.hierarchyViewReference;
    const { items } = await this.client.instances.aggregate({
      view,
      instanceType: 'node',
      aggregates: [{ count: { property: 'externalId' } }],
      groupBy: ['parent'],
      filter: {
        and: [
          {
            in: {
              property: this.hierarchyViewProperty('parent', view),
              values: parents,
            },
          },
          {
            equals: {
              property: ['node', 'space'],
              value: this.instanceSpace,
            },
          },
        ],
      },
      limit,
    });
    return mapInstanceAggregateItemsToParentChildCounts(items);
  }

  async getInstancesByIds(
    ids: DirectRelationReference[],
    options: Pick<HierarchyQueryOptions, 'viewReference'> = {}
  ) {
    if (ids.length === 0) return [];
    const view = options.viewReference ?? this.hierarchyViewReference;
    const { items } = await this.client.instances.retrieve({
      sources: [{ source: view }],
      items: ids.map((id) => ({ ...id, instanceType: 'node' })),
    });
    return items;
  }
}

export const createCdmAssetService = (
  ...args: ConstructorParameters<typeof CdmAssetService>
): IAssetService => new CdmAssetService(...args);
